import * as React from "react";
import { connect } from "react-redux";
import {
    dashboardWizardWebSourceChanged,
    dashboardWizardDurationChanged,
    dashboardWizardCountryChanged,
    fetchMetricMetaData,
    dashboardWizardFilterChanged,
    dashboardWizardKeyChanged,
    dashboardWizardKeyAppend,
    dashboardWizardKeyRemove,
    dashboardWizardSelectedChannelChanged,
    dashboardWizardWidgetTypeChanged,
    updateGAVerifiedDataFilter,
    dashboardWizardMetricAndFamilyChanged,
    dashboardWizardDeviceChanged,
} from "./actions/dashboardWizardActionCreators";
import { allTrackers } from "app/services/track/track";
import DashboardWizardAutocomplete from "./components/DashboardWizardAutocomplete";
import DashboardWizardTrafficSources from "./components/DashboardWizardTrafficSources";
import DashboardWizardDuration from "./components/DashboardWizardDuration";
import DashboardWizardDynamicFilters from "./components/DashboardWizardDynamicFilters";
import DashboardWizardCountry from "./components/DashboardWizardCountry";
import DashboardWizardChannelSelection from "./components/DashboardWizardChannelSelection";
import DashboardWizardMetrics from "./components/DashboardWizardMetrics";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import DashboardWizardDevice from "app/components/dashboard/widget-wizard/components/DashboardWizardDevice";
import SegmentDropdowns from "./components/segments/SegmentDropdowns";
import { SEGMENTS_FAMILY_NAME } from "app/components/widget/widget-utilities/segments/constants";
import { updateUniqueSegmentsIds } from "app/actions/segmentsModuleActions";
import MarketAnalysisDropdowns from "./components/MarketAnalysis/MarketAnalysisDropdowns";
import { MARKET_SHARES_METRIC_TYPE } from "./components/MarketAnalysis/constants";

class DashboardWizard extends React.Component<any, any> {
    constructor(props) {
        super(props);

        // When a user selects new metric - we have to make sure that widgetType action is dispatched in order
        // to update the avilable filters
        props.changeWidgetType(
            {
                type: props.customDashboard.widget.type,
                id: props.customDashboard.selectedWidgetType,
            },
            props.customDashboard,
        );

        if (props.customDashboard.widget.key.length > 0) {
            props.updateGAVerifiedDataFilter(props.customDashboard.widget.key, props.customDashboard);
        }
    }

    public componentDidMount() {
        const newWidget = this.props.customDashboard.widget;

        if (newWidget.family === SEGMENTS_FAMILY_NAME) {
            if (newWidget?.filters?.segmentId && !newWidget?.key[0]?.segmentId) {
                const newKey = [{ ...newWidget?.key[0], segmentId: newWidget?.filters?.segmentId }];
                this.props.changeKey(newKey, this.props.customDashboard);
            }
        }
    }

    public componentDidUpdate(prevProps) {
        const prevWidget = prevProps.customDashboard.widget;
        const newWidget = this.props.customDashboard.widget;

        // If user action triggered a webSource change - we have to make sure the webSource action is dispatched in order
        // to update the available durations.
        if (prevWidget.webSource !== newWidget.webSource) {
            if (newWidget.family !== "Mobile") {
                return this.props.changeWebSource(newWidget.webSource, this.props.customDashboard);
            }
        }

        // If user action triggered a comparedDuration change - we have to make sure the changeKey action is dispatched in order
        // to update the available widget types.
        if (prevWidget.comparedDuration !== newWidget.comparedDuration) {
            this.props.changeKey(newWidget.key, this.props.customDashboard);
            if (newWidget.family !== "Mobile" && newWidget.key.length > 0) {
                this.props.updateGAVerifiedDataFilter(newWidget.key, this.props.customDashboard);
                return;
            }
        }

        // If user action triggered a widgetType change - we have to make sure the updateGAVerifiedDataFilter action is dispatched in order
        // to update the GAVerified flag.
        if (prevWidget.type !== newWidget.type || prevWidget.metric !== newWidget.metric) {
            if (newWidget.family === "Website" && newWidget.key.length > 0) {
                return this.props.updateGAVerifiedDataFilter(newWidget.key, this.props.customDashboard);
            }
        }
    }

    private changeCountry = (itemId) => {
        this.props.changeCountry(itemId, this.props.customDashboard);
    };

    private changeMetric = (metricId) => {
        this.props.changeMetric(metricId, this.props.customDashboard);
    };

    private changeTrafficSource = (item) => {
        allTrackers.trackEvent("Capsule Button", "switch", `Data Source/${item}`);
        this.props.changeWebSource(item, this.props.customDashboard);
    };

    private changeChannel = (item) => {
        allTrackers.trackEvent("Drop Down", "click", `Channel/${item.text}`);
        this.props.changeSelectedChannel(item, this.props.customDashboard);
    };

    private changeDevice = (item) => {
        allTrackers.trackEvent("Drop Down", "click", `Device/${item.text}`);
        this.props.changeDevice(item, this.props.customDashboard);
    };

    private changeFilter = (item) => {
        if (!Array.isArray(item)) {
            allTrackers.trackEvent("Drop Down", "click", `${item.name}/${item.text}`);
        }
        this.props.changeFilter(item, this.props.customDashboard);
    };

    render() {
        const customDashboard = this.props?.customDashboard;
        const isSegmentsGroup = customDashboard?.isCompare && customDashboard?.widget?.family === SEGMENTS_FAMILY_NAME;
        const isMarketShares = customDashboard.widget.metric === MARKET_SHARES_METRIC_TYPE;

        return (
            <div>
                <h4>
                    <I18n>home.dashboards.wizard.metricType.title</I18n>
                </h4>
                <DashboardWizardMetrics
                    changeMetric={this.changeMetric}
                    family={this.props.customDashboard.widget.family}
                    selectedMetric={this.props.customDashboard.widget.metric}
                    isSegmentsGroup={isSegmentsGroup}
                    selectedCountry={this.props.customDashboard.widget.country}
                    webSource={this.props.customDashboard.widget.webSource}
                    wrapperClassName={"metricType"}
                />
                {isSegmentsGroup ? <SegmentDropdowns customDashboard={this.props.customDashboard} /> : null}
                {!isSegmentsGroup && !isMarketShares ? (
                    <DashboardWizardAutocomplete
                        fetchMetricMetaData={this.props.fetchMetricMetaData}
                        keyAppend={this.props.keyAppend}
                        updateUniqueSegments={this.props.updateUniqueSegments}
                        keyRemove={this.props.keyRemove}
                        widgetFilters={this.props.customDashboard.widget.filters}
                        changeFilter={this.changeFilter}
                        updateGAVerifiedDataFilter={this.props.updateGAVerifiedDataFilter}
                        customDashboard={this.props.customDashboard}
                    />
                ) : null}
                {isMarketShares ? <MarketAnalysisDropdowns customDashboard={this.props.customDashboard} /> : null}
                <DashboardWizardTrafficSources
                    isActive={this.props.customDashboard.formElements.webSource.isActive}
                    availableTrafficSources={this.props.customDashboard.availableTrafficSources}
                    selectedTrafficSource={this.props.customDashboard.widget.webSource}
                    changeTrafficSource={this.changeTrafficSource}
                />
                <div
                    key="filtersContainer"
                    className="filters"
                >
                    <DashboardWizardDuration
                        changeDuration={this.props.changeDuration}
                        customDashboard={this.props.customDashboard}
                    />
                    <DashboardWizardCountry
                        key={this.props.customDashboard.widget.country}
                        changeCountry={this.changeCountry}
                        availableCountries={this.props.customDashboard.availableCountries}
                        selectedCountryId={this.props.customDashboard.widget.country}
                        titleComponent={
                            <h5>
                                <I18n>home.dashboards.wizard.country.title</I18n>
                            </h5>
                        }
                        disabled={this.props.customDashboard.disableCountryFilter}
                        isHidden={this.props.customDashboard.isCountryFilterHidden}
                    />
                </div>
                <DashboardWizardChannelSelection
                    isActive={this.props.customDashboard.formElements.trafficChannels.isActive}
                    selectedChannel={this.props.customDashboard.widget.selectedChannel}
                    changeChannel={this.changeChannel}
                    widgetWebSource={this.props.customDashboard.widget.webSource}
                    widgetDuration={this.props.customDashboard.widget.duration}
                />
                <DashboardWizardDevice
                    isActive={this.props.customDashboard.formElements.device.isActive}
                    selectedDevice={this.props.customDashboard.widget.device}
                    widgetKeys={this.props.customDashboard.widget.key}
                    changeDevice={this.changeDevice}
                    navigationState={this.props.customDashboard.widget.navigationState}
                />
                <DashboardWizardDynamicFilters
                    availableFilters={this.props.customDashboard.availableFilters}
                    widgetFilters={this.props.customDashboard.widget.filters}
                    widgetMetric={this.props.customDashboard.widget.metric}
                    widgetWebSource={this.props.customDashboard.widget.webSource}
                    widgetDuration={this.props.customDashboard.widget.duration}
                    changeFilter={this.changeFilter}
                    widgetType={this.props.customDashboard.widget.type}
                    widgetPreview={this.props.widgetPreview}
                    widgetKeys={this.props.customDashboard.widget.key}
                    widgetCountry={this.props.customDashboard.widget.country}
                />
            </div>
        );
    }
}

function mapDispatchToProps(dispatch) {
    return {
        fetchMetricMetaData: (newKey, customDashboard) => {
            dispatch(fetchMetricMetaData(customDashboard, newKey));
        },
        changeKey: (newKey, customDashboard) => {
            dispatch(dashboardWizardKeyChanged(customDashboard, newKey));
        },
        keyAppend: (newKey, customDashboard) => {
            dispatch(dashboardWizardKeyAppend(customDashboard, newKey));
        },
        keyRemove: (newKey, customDashboard) => {
            dispatch(dashboardWizardKeyRemove(customDashboard, newKey));
        },
        changeWebSource: (newWebSource, customDashboard) => {
            dispatch(dashboardWizardWebSourceChanged(customDashboard, newWebSource));
        },
        changeWidgetType: (newWidgetType, customDashboard) => {
            dispatch(dashboardWizardWidgetTypeChanged(customDashboard, newWidgetType));
        },
        changeDuration: (newDuration, customDashboard, comparedDuration) => {
            dispatch(dashboardWizardDurationChanged(customDashboard, newDuration, comparedDuration));
        },
        changeCountry: (newCountry, customDashboard) => {
            dispatch(dashboardWizardCountryChanged(customDashboard, newCountry));
        },
        changeMetric: (metric, customDashboard) => {
            dispatch(
                dashboardWizardMetricAndFamilyChanged(
                    customDashboard,
                    metric,
                    customDashboard.widget.family,
                    customDashboard.widget.key,
                ),
            );
        },
        changeFilter: (newFilter, customDashboard) => {
            dispatch(dashboardWizardFilterChanged(customDashboard, newFilter));
        },
        changeSelectedChannel: (newSelectedChannel, customDashboard) => {
            dispatch(dashboardWizardSelectedChannelChanged(customDashboard, newSelectedChannel));
        },
        changeDevice: (newDevice, customDashboard) => {
            dispatch(dashboardWizardDeviceChanged(customDashboard, newDevice));
        },
        updateGAVerifiedDataFilter: (key, customDashboard) => {
            dispatch(updateGAVerifiedDataFilter(customDashboard, key));
        },
        updateUniqueSegments: (uniqueSegments) => {
            dispatch(updateUniqueSegmentsIds(uniqueSegments));
        },
    };
}

function mapStateToProps({ customDashboard: { dashboardWizard } }) {
    return {
        customDashboard: dashboardWizard,
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(DashboardWizard);
